import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from 'react-simple-maps';
import Content from '../../components/Content';
import { getAnnualReportData } from '../../redux/actions/annualReportActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';
import styles from './AnnualReport.module.css';

// World topology JSON URL 
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// ISO-3 alpha code to numeric ISO code mapping (for matching with world-atlas topology)
const ISO3_TO_NUMERIC = {
  "USA": "840", "GBR": "826", "RUS": "643", "CHN": "156", "JPN": "392",
  "DEU": "276", "FRA": "250", "ITA": "380", "ESP": "724", "CAN": "124",
  "AUS": "036", "BRA": "076", "IND": "356", "MEX": "484", "NLD": "528",
  "IRL": "372", "SGP": "702", "CHE": "756", "ZAF": "710", "NOR": "578",
  "SWE": "752", "ARE": "784", "MYS": "458", "CHL": "152", "AUT": "040",
  "FIN": "246", "NZL": "554", "ARG": "032", "ISR": "376", "PER": "604",
  "IDN": "360", "TWN": "158", "POL": "616", "DNK": "208", "COL": "170",
  "LUX": "442", "BGR": "100", "PHL": "608", "PAN": "591", "BEL": "056",
  "PRY": "600", "URY": "858", "ECU": "218", "JAM": "388", "GIN": "324",
  "GTM": "320", "VEN": "862", "HND": "340", "PRT": "620", "DMA": "212",
  "UKR": "804", "TZA": "834", "BOL": "068", "IRN": "364", "SYR": "760",
  "LAO": "418", "VNM": "704", "MDA": "498", "MKD": "807", "COD": "180",
  "CIV": "384", "THA": "764", "EGY": "818", "SAU": "682", "PAK": "586",
  "BGD": "050", "NGA": "566", "KEN": "404", "CZE": "203", "ROU": "642",
  "HUN": "348", "GRC": "300", "HKG": "344", "KOR": "410", "PRK": "408"
};

// Frontline Red color scale
const colorScale = (value, maxValue) => {
  if (!value || value === 0) return '#e0e0e0'; // No data
  
  const normalized = Math.log10(value + 1) / Math.log10(maxValue + 1);
  
  // Frontline red gradient: white -> light pink -> pink -> red -> dark red
  const colors = [
    { pos: 0, r: 255, g: 255, b: 255 },     // #ffffff
    { pos: 0.2, r: 254, g: 229, b: 229 },   // #fee5e5
    { pos: 0.4, r: 242, g: 182, b: 182 },   // #f2b6b6
    { pos: 0.6, r: 224, g: 90, b: 90 },     // #e05a5a
    { pos: 0.8, r: 197, g: 2, b: 2 },       // #c50202
    { pos: 1, r: 139, g: 0, b: 0 }          // #8b0000
  ];
  
  // Find the two colors to interpolate between
  let lower = colors[0];
  let upper = colors[colors.length - 1];
  
  for (let i = 0; i < colors.length - 1; i++) {
    if (normalized >= colors[i].pos && normalized <= colors[i + 1].pos) {
      lower = colors[i];
      upper = colors[i + 1];
      break;
    }
  }
  
  // Interpolate
  const range = upper.pos - lower.pos;
  const factor = range === 0 ? 0 : (normalized - lower.pos) / range;
  
  const r = Math.round(lower.r + (upper.r - lower.r) * factor);
  const g = Math.round(lower.g + (upper.g - lower.g) * factor);
  const b = Math.round(lower.b + (upper.b - lower.b) * factor);
  
  return `rgb(${r}, ${g}, ${b})`;
};

const FRONTEND_ANNUAL_REPORT_ERROR_CODES = {
  INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  DATA_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildAnnualReportSupportError = (errorCode, pageInfo, sourceMessage) =>
  formatFrontendSupportError({
    errorCode,
    pageInfo,
    sourceMessage,
  });

const getErrorMessage = (source, fallbackMessage) => {
  if (typeof source === 'string' && source.trim()) {
    return source.trim();
  }

  if (source && typeof source === 'object') {
    if (typeof source.msg === 'string' && source.msg.trim()) {
      return source.msg.trim();
    }
    if (typeof source.message === 'string' && source.message.trim()) {
      return source.message.trim();
    }
  }

  return fallbackMessage;
};

const WorldHeatMap = ({ countryData }) => {
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  
  // Create a lookup map for quick access (convert ISO-3 to numeric for matching)
  const dataMap = useMemo(() => {
    const map = {};
    countryData.forEach(item => {
      // Store by numeric ID for world-atlas topology matching
      const numericId = ISO3_TO_NUMERIC[item.id];
      if (numericId) {
        map[numericId] = item;
      }
      // Also store by ISO-3 for fallback
      map[item.id] = item;
    });
    return map;
  }, [countryData]);
  
  // Get max value for color scaling
  const maxValue = useMemo(() => {
    return Math.max(...countryData.map(d => d.count), 1);
  }, [countryData]);
  
  const handleMouseEnter = (geo, event) => {
    const countryId = geo.id;
    const data = dataMap[countryId];
    
    // Get country name from data or geo properties
    const countryName = data?.country || geo.properties?.name || geo.properties?.NAME || countryId;
    
    if (data) {
      setTooltipContent(`${countryName}: ${data.count.toLocaleString()} connections`);
    } else {
      setTooltipContent(`${countryName}: No data`);
    }
    
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };
  
  const handleMouseLeave = () => {
    setTooltipContent('');
  };
  
  const handleMouseMove = (event) => {
    if (tooltipContent) {
      setTooltipPosition({ x: event.clientX, y: event.clientY });
    }
  };
  
  return (
    <div className={styles.mapWrapper} onMouseMove={handleMouseMove}>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 140,
          center: [0, 30]
        }}
        style={{ width: '100%', height: '100%' }}
      >
        <ZoomableGroup>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies
                .filter(geo => geo.id !== "010") // Remove Antarctica
                .map(geo => {
                const countryId = geo.id;
                const data = dataMap[countryId];
                const fillColor = data 
                  ? colorScale(data.count, maxValue)
                  : '#e0e0e0';
                
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={(event) => handleMouseEnter(geo, event)}
                    onMouseLeave={handleMouseLeave}
                    style={{
                      default: {
                        fill: fillColor,
                        stroke: '#999999',
                        strokeWidth: 0.5,
                        outline: 'none'
                      },
                      hover: {
                        fill: data ? '#ff4444' : '#d0d0d0',
                        stroke: '#333333',
                        strokeWidth: 1,
                        outline: 'none',
                        cursor: 'pointer'
                      },
                      pressed: {
                        fill: fillColor,
                        outline: 'none'
                      }
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>
      
      {/* Legend */}
      <div className={styles.mapLegend}>
        <span className={styles.legendLabel}>Low</span>
        <div className={styles.legendGradient}></div>
        <span className={styles.legendLabel}>High</span>
      </div>
      
      {/* Tooltip */}
      {tooltipContent && (
        <div 
          className={styles.mapTooltip}
          style={{ 
            left: tooltipPosition.x + 10, 
            top: tooltipPosition.y - 30 
          }}
        >
          {tooltipContent}
        </div>
      )}
    </div>
  );
};

const AnnualReport = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [selectedYear] = useState(String(new Date().getFullYear()));
  const [supportErrorMessage, setSupportErrorMessage] = useState('');
  const [copiedSupportError, setCopiedSupportError] = useState(false);

  const setAnnualReportSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
    setSupportErrorMessage(buildAnnualReportSupportError(errorCode, pageInfo, sourceMessage));
    setCopiedSupportError(false);
  }, []);

  const clearAnnualReportSupportError = React.useCallback((force = false) => {
    if (!force) {
      return;
    }
    setSupportErrorMessage('');
    setCopiedSupportError(false);
  }, []);

  const handleCopySupportError = React.useCallback(async () => {
    if (!supportErrorMessage) return;
    try {
      await navigator.clipboard.writeText(supportErrorMessage);
      setCopiedSupportError(true);
      setTimeout(() => setCopiedSupportError(false), 2000);
    } catch (copyError) {
      setCopiedSupportError(false);
    }
  }, [supportErrorMessage]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(false);
      
      try {
        const data = await dispatch(getAnnualReportData(selectedYear));
        
        if (data && data.status) {
          setReportData(data);
          setError(false);
          clearAnnualReportSupportError(true);
        } else {
          setReportData(null);
          setError(true);
          setAnnualReportSupportError(
            FRONTEND_ANNUAL_REPORT_ERROR_CODES.DATA_LOAD_FAILED,
            'Annual Report Page / Data Load Failed',
            getErrorMessage(data, 'Annual report request did not return success')
          );
        }
      } catch (err) {
        setReportData(null);
        setError(true);
        setAnnualReportSupportError(
          FRONTEND_ANNUAL_REPORT_ERROR_CODES.INITIAL_LOAD_FAILED,
          'Annual Report Page / Data Request Error',
          getErrorMessage(err, 'Error fetching annual report data')
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [clearAnnualReportSupportError, dispatch, selectedYear, setAnnualReportSupportError]);

  const formatNumber = (num) => {
    // Coerce missing/non-numeric values to 0 so a gap in the report payload
    // renders as "0" instead of throwing (undefined.toString() crashed the page).
    const n = Number(num);
    if (!Number.isFinite(n)) {
      return '0';
    }
    if (n >= 1000000) {
      return `${(n / 1000000).toFixed(1)}M`;
    } else if (n >= 1000) {
      return `${(n / 1000).toFixed(1)}K`;
    }
    return n.toString();
  };

  const formatFullNumber = (num) => {
    const n = Number(num);
    return Number.isFinite(n) ? n.toLocaleString() : '0';
  };

  if (loading) {
    return (
      <Content>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <div className={styles.loadingText}>Loading Annual Report Data...</div>
        </div>
      </Content>
    );
  }

  // The report is served but has no data yet (the generation engine is still
  // pending on the backend, which returns an empty report + a message). Show
  // that message instead of a page full of zeros — or a crash on the missing
  // numeric fields.
  const reportHasData = reportData && (
    reportData.connection_events !== undefined ||
    reportData.security_events !== undefined ||
    reportData.assets_monitored !== undefined
  );
  if (!error && reportData && !reportHasData) {
    return (
      <Content>
        <div className={styles.errorContainer}>
          <div className={styles.errorText}>
            {reportData.msg || 'The annual report is not available yet. Please check back later.'}
          </div>
        </div>
      </Content>
    );
  }

  if (error || !reportData) {
    return (
      <Content>
        <div className={styles.errorContainer}>
          <div className={styles.errorText}>
            Failed to load annual report data. Please try again later.
          </div>
          {supportErrorMessage && (
            <div className={styles.supportErrorCard}>
              <strong>Support Error:</strong>
              <code className={styles.supportErrorCode}>{supportErrorMessage}</code>
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                <button
                  type='button'
                  className={styles.copySupportErrorButton}
                  onClick={handleCopySupportError}
                >
                  {copiedSupportError ? 'Copied' : 'Copy Error Details'}
                </button>
                <button
                  type='button'
                  className={styles.copySupportErrorButton}
                  onClick={() => clearAnnualReportSupportError(true)}
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>
      </Content>
    );
  }

  return (
    <Content>
      <div className={styles.container}>
        {supportErrorMessage && (
          <div className={styles.supportErrorBanner}>
            <div className={styles.supportErrorBannerText}>
              <strong>Support Error:</strong>
              <code className={styles.supportErrorCode}>{supportErrorMessage}</code>
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <button
                type='button'
                className={styles.copySupportErrorButton}
                onClick={handleCopySupportError}
              >
                {copiedSupportError ? 'Copied' : 'Copy Error Details'}
              </button>
              <button
                type='button'
                className={styles.copySupportErrorButton}
                onClick={() => clearAnnualReportSupportError(true)}
              >
                Clear
              </button>
            </div>
          </div>
        )}
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div>
              <div className={styles.headerTitle}>
                WATCHER | FRONTLINE CYBER SOLUTIONS — {reportData.company_name || 'CLIENT'} Security
              </div>
              <div className={styles.headerSubtitle}>
                Comprehensive Network Security Monitoring & Analysis
              </div>
            </div>
            <div className={styles.yearBadge}>
              {reportData.year} Annual Data
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className={styles.gridContainer}>
          {/* Left Column Top - Connection Events */}
          <div className={styles.statCard}>
            <div className={styles.statCardTitle}>CONNECTION EVENTS</div>
            <div className={styles.statCardValue}>
              {formatNumber(reportData.connection_events)}
            </div>
            <div className={styles.statCardSubtext}>
              {formatFullNumber(reportData.connection_events)} total events
            </div>
          </div>

          {/* Left Column Bottom - Security Events */}
          <div className={styles.statCard}>
            <div className={styles.statCardTitle}>SECURITY EVENTS</div>
            <div className={styles.statCardValue}>
              {formatNumber(reportData.security_events)}*
            </div>
            <div className={styles.statCardSubtext}>
              {formatFullNumber(reportData.security_events)} total alerts
            </div>
          </div>

          {/* Center - Geo Heat Map */}
          <div className={styles.mapCard}>
            <div className={styles.mapCardTitle}>GEO HEAT MAP OF CONNECTIONS</div>
            <div className={styles.mapContainer}>
              {reportData.country_data && reportData.country_data.length > 0 ? (
                <WorldHeatMap countryData={reportData.country_data} />
              ) : (
                <div className={styles.noHeatmapData}>
                  <p>No geographic data available for heatmap visualization</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column Top - IOC and Signatures */}
          <div className={styles.statCard}>
            <div className={styles.statCardTitle}>IOC AND SIGNATURES MONITORED</div>
            <div className={styles.multiStatCard}>
              <div className={styles.statLine}>
                <span className={styles.statLabel}>IOCs</span>
                <span className={styles.statValue}>
                  {formatNumber(reportData.ioc_count)}
                </span>
              </div>
              <div className={styles.statLine}>
                <span className={styles.statLabel}>Signatures</span>
                <span className={styles.statValue}>
                  {formatNumber(reportData.signature_count)}
                </span>
              </div>
            </div>
          </div>

          {/* Right Column Bottom - Assets Monitored */}
          <div className={styles.statCard}>
            <div className={styles.statCardTitle}>ASSETS MONITORED AND PROTECTED</div>
            <div className={styles.statCardValue}>
              {formatNumber(reportData.assets_monitored)}
            </div>
            <div className={styles.statCardSubtext}>
              {formatFullNumber(reportData.assets_monitored)} unique devices
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className={styles.disclaimer}>
          <p className={styles.disclaimerText}>
            <span className={styles.disclaimerAsterisk}>*</span>
            All identified events were investigated and remediated in compliance with the agreed-upon{' '}
            <em>operational</em> processes between {reportData.company_name || 'CLIENT'} and the FrontLine CSOC.
          </p>
        </div>
      </div>
    </Content>
  );
};

export default AnnualReport;
